import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

/**
 * Audio Library — Prabhat Samgiita, Baba's voice, kiirtan and more.
 * previewSec is the admin's dial: blank = plays free in full; a number
 * (7, 30…) is how many seconds play before the app asks for a
 * subscription. The public SITE does not enforce the gate yet — by the
 * owner's explicit instruction it stays as-is until the app ships.
 */
const EMPTY = {
  title: '', attribution: '', category: 'kiirtan', fileUrl: '', cover: '',
  durationSec: '', previewSec: '', gated: false, sourceUrl: '', published: true,
};
const CATEGORIES = [
  { value: 'prabhat-samgiita', label: 'Prabhat Samgiita' },
  { value: 'baba-audio', label: "Baba's Voice (audio)" },
  { value: 'kiirtan', label: 'Kiirtan' },
  { value: 'other', label: 'Other' },
];

const AudioManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => { fetchRows(); }, []);

  const fetchRows = async () => {
    try {
      const res = await api.get('/media/audio?all=1');
      setRows(res.data.audios || []);
    } catch (err) { console.error('Failed to fetch audio', err); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setFormData(EMPTY); setShowModal(true); };
  const openEdit = (r) => {
    setEditId(r._id);
    setFormData({ ...EMPTY, ...r, durationSec: r.durationSec ?? '', previewSec: r.previewSec ?? '' });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { alert('Title is required.'); return; }
    const payload = {
      ...formData,
      durationSec: formData.durationSec === '' ? null : Number(formData.durationSec),
      previewSec: formData.previewSec === '' ? null : Number(formData.previewSec),
      gated: formData.previewSec !== '' && Number(formData.previewSec) >= 0 ? formData.gated : false,
    };
    try {
      if (editId) await api.put(`/media/audio/${editId}`, payload);
      else await api.post('/media/audio', payload);
      setShowModal(false); fetchRows();
    } catch (err) { alert(err.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/media/audio/${r._id}`); fetchRows(); }
    catch (err) { alert('Delete failed'); }
  };

  const shown = filter ? rows.filter((r) => r.category === filter) : rows;
  const field = 'w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2A2858]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Audio Library</h1>
          <p className="text-sm opacity-70">Prabhat Samgiita · Baba's voice · Kiirtan — with the preview-seconds dial for the app's subscription gate.</p>
        </div>
        <button type="button" onClick={openCreate} className="bg-[#2A2858] text-white font-bold px-5 py-3 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">+ New Audio</button>
      </div>

      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => setFilter('')} className={`px-4 py-2 rounded-xl text-xs font-bold ${filter === '' ? 'bg-[#2A2858] text-white' : 'bg-stone-100'}`}>All</button>
        {CATEGORIES.map((c) => (
          <button type="button" key={c.value} onClick={() => setFilter(c.value)} className={`px-4 py-2 rounded-xl text-xs font-bold ${filter === c.value ? 'bg-[#2A2858] text-white' : 'bg-stone-100'}`}>{c.label}</button>
        ))}
      </div>

      {loading ? <p className="opacity-70">Loading…</p> : shown.length === 0 ? (
        <div className="rounded-xl border border-stone-200 p-8 text-center opacity-80">No audio in this shelf yet — upload the first one above.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-stone-200">
              <th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Preview</th><th className="p-3">Gate</th><th className="p-3">File</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r._id} className="border-b border-stone-100 last:border-0">
                  <td className="p-3 font-medium">{r.title}{r.attribution ? <span className="opacity-60"> — {r.attribution}</span> : null}</td>
                  <td className="p-3">{(CATEGORIES.find((c) => c.value === r.category) || {}).label || r.category}</td>
                  <td className="p-3">{r.previewSec == null ? 'Full free' : `${r.previewSec}s`}</td>
                  <td className="p-3">{r.gated ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">Subscription</span> : <span className="opacity-50">open</span>}</td>
                  <td className="p-3">{r.fileUrl ? <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" className="text-[#2A2858] underline">file</a> : <span className="opacity-50">none</span>}</td>
                  <td className="p-3"><span className={`px-3 py-1 rounded-full text-xs font-bold ${r.published ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>{r.published ? 'Published' : 'Draft'}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => openEdit(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-stone-100 hover:bg-stone-200 mr-2">Edit</button>
                    <button type="button" onClick={() => handleDelete(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editId ? 'Edit Audio' : 'New Audio'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <input name="title" value={formData.title} onChange={handleChange} placeholder="Title *" className={field} />
              <div className="grid grid-cols-2 gap-4">
                <input name="attribution" value={formData.attribution} onChange={handleChange} placeholder="Artist / occasion / archive note" className={field} />
                <select name="category" value={formData.category} onChange={handleChange} className={field}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <input name="fileUrl" value={formData.fileUrl} onChange={handleChange} placeholder="Audio file URL (uploaded file)" className={field} />
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-sm">Duration (seconds)
                  <input type="number" name="durationSec" value={formData.durationSec} onChange={handleChange} placeholder="e.g. 264" className={field} />
                </label>
                <label className="block text-sm">Preview seconds (blank = full free)
                  <input type="number" name="previewSec" value={formData.previewSec} onChange={handleChange} placeholder="e.g. 7" className={field} />
                </label>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="gated" checked={formData.gated} onChange={handleChange} /> Ask subscription after preview (app)</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="published" checked={formData.published} onChange={handleChange} /> Published</label>
              </div>
              <div>
                <p className="text-sm mb-2">Cover (optional)</p>
                <ImageUploader onUploadSuccess={(url) => setFormData((f) => ({ ...f, cover: url }))} />
              </div>
              <input name="sourceUrl" value={formData.sourceUrl} onChange={handleChange} placeholder="Source URL / provenance" className={field} />
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-stone-100 text-stone-500 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
                <button type="submit" className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">{editId ? 'Update Audio' : 'Create Audio'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioManager;
